import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Ultra-subtle diagonal tiled watermark of the logged-in user's email — an
 * anti-leak deterrent for live client demos. Deliberately understated: opacity
 * 0.018, small type, generous letter spacing, wider vertical rhythm. Sits
 * behind interactive content (pointer-events off, z-index below toasts).
 *
 * Super-admins can press ⌘/Ctrl+Shift+W to hide it while capturing internal
 * screenshots; the toggle survives across pages but resets on refresh so the
 * next login pitch is never accidentally screen-recorded without it.
 */
const HIDE_STORAGE_KEY = "kolradar:watermark-hidden";

export default function EmailWatermark() {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => {
    try { return sessionStorage.getItem(HIDE_STORAGE_KEY) === "1"; } catch { return false; }
  });

  // Super-admin keyboard shortcut — ⌘/Ctrl + Shift + W toggles visibility.
  useEffect(() => {
    if (!user?.is_super_admin) return;
    const onKey = (e) => {
      const key = (e.key || "").toLowerCase();
      const combo = (e.metaKey || e.ctrlKey) && e.shiftKey && key === "w";
      if (!combo) return;
      e.preventDefault();
      setHidden((h) => {
        const next = !h;
        try { sessionStorage.setItem(HIDE_STORAGE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
        toast.success(next ? "Watermark hidden · resumes next session" : "Watermark visible");
        return next;
      });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user?.is_super_admin]);

  const email = user?.email;
  if (!email || hidden) return null;

  // Tile pattern — one row string, repeated vertically with generous gap.
  const rowText = `  ${email}  •  KOL RADAR  •  `.repeat(14);
  const rows = Array.from({ length: 14 });

  return (
    <div
      data-testid="email-watermark"
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: "-50%",
        width: "200%",
        height: "200%",
        transform: "rotate(-24deg)",
        transformOrigin: "center",
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 40, // above content, below toasts/modals (z-50+)
        opacity: 0.018,
        color: "#F8FAFC",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, monospace",
        fontSize: 11,
        lineHeight: "120px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        letterSpacing: "0.28em",
        fontWeight: 400,
      }}
    >
      {rows.map((_, i) => (
        <div key={i}>{rowText}</div>
      ))}
    </div>
  );
}
